// Real-time collaboration client.
// Connects to a PartyServer/PartyKit room over WebSocket, broadcasts local state deltas,
// receives remote deltas (with sender attribution for the ripple animation),
// and tracks presence. Late joiners get a one-shot full-state sync from any
// existing peer.

import PartySocket from 'partysocket';

export type Delta =
  | { type: 'step';  ti: number; s: number; on: boolean; pitch?: number }
  | { type: 'pitch'; ti: number; s: number; pitch: number }
  | { type: 'bpm';   bpm: number }
  | { type: 'voice'; timbre: string }
  | { type: 'scale'; scale: string }
  | { type: 'tonesource'; source: string }
  | { type: 'volume'; key: string; v: number }
  | { type: 'decay'; d: number }
  | { type: 'drumdecay'; key: string; d: number }
  | { type: 'octave'; semis: number }
  | { type: 'glide'; g: number }
  | { type: 'play';  playing: boolean }
  | { type: 'name';  name: string }
  | { type: 'full';  hash: string };

export type Peer = { id: string; color: string };

export type CollabHandlers = {
  onPresence: (you: Peer | null, others: Peer[]) => void;
  onRemoteDelta: (delta: Delta, from: Peer) => void;
  // Server asked us to send the current state so a late joiner can catch up.
  // The handler returns a `full` delta describing the current state.
  onSyncRequest: () => Delta;
  // Late joiner: received a full state from a peer. Apply silently.
  onSyncState: (delta: Delta) => void;
  // A peer (identified by their connection id) fired a live emoji reaction.
  onEmoji: (peerId: string, emoji: string) => void;
};

// Set VITE_PARTYSERVER_HOST in Vercel once the Cloudflare relay is deployed.
// Keep the legacy variable/default during the staged migration; these are not
// an automatic fallback between backends (which would split active rooms).
const DEFAULT_HOST = 'beatswarm.bhollifield.partykit.dev';
const DEFAULT_DEV_HOST = '127.0.0.1:1999';

export function getPartyHost(): string {
  const env = import.meta.env;
  const explicit = (env?.VITE_PARTYSERVER_HOST || env?.VITE_PARTYKIT_HOST) as string | undefined;
  if (explicit) return explicit;
  if (env?.DEV) return DEFAULT_DEV_HOST;
  return DEFAULT_HOST;
}

export class Collab {
  private socket: PartySocket | null = null;
  private handlers: CollabHandlers;
  private suppressBroadcast = false;
  me: Peer | null = null;
  others: Peer[] = [];
  roomId: string | null = null;
  // Local display name (from localStorage). Re-announced to the room on join
  // and whenever a new peer appears, since the relay keeps no server state and
  // late joiners would otherwise never learn it.
  localName: string | null = null;
  private lastRosterSize = 0;

  constructor(handlers: CollabHandlers) {
    this.handlers = handlers;
  }

  connect(roomId: string): void {
    if (this.socket && this.roomId === roomId) return;
    if (this.socket) this.disconnect();
    this.roomId = roomId;
    const host = getPartyHost();
    this.socket = new PartySocket({
      host,
      room: roomId,
      // partysocket has its own reconnect/backoff; defaults are fine.
    });
    this.socket.addEventListener('message', (e: MessageEvent) => this.handleMessage(e.data));
    this.socket.addEventListener('close', () => {
      // Don't update presence aggressively on transient disconnects — let
      // partysocket retry quietly. If the server-side close was permanent,
      // a fresh `welcome` on reconnect will overwrite our state anyway.
    });
  }

  disconnect(): void {
    this.socket?.close();
    this.socket = null;
    this.me = null;
    this.others = [];
    this.roomId = null;
    this.lastRosterSize = 0;
  }

  /** Set the local display name and broadcast it to the room. */
  setLocalName(name: string): void {
    this.localName = name;
    this.send({ type: 'name', name });
  }

  /** Fire a live emoji reaction to the room. Server stamps the sender id. */
  sendEmoji(emoji: string): void {
    if (!this.socket) return;
    try {
      this.socket.send(JSON.stringify({ type: 'emoji', emoji }));
    } catch {
      /* socket not open — drop silently */
    }
  }

  private maybeBroadcastName(): void {
    if (this.localName) this.send({ type: 'name', name: this.localName });
  }

  isConnected(): boolean {
    return !!this.socket;
  }

  /** Send a local delta to the room. Suppressed while applying a remote delta. */
  send(delta: Delta): void {
    if (this.suppressBroadcast) return;
    if (!this.socket) return;
    try {
      this.socket.send(JSON.stringify({ type: 'delta', payload: delta }));
    } catch {
      /* socket not yet open or closing — drop the delta silently */
    }
  }

  /**
   * Run `fn` with broadcasting suppressed. Used to apply incoming remote
   * deltas without echoing them back to the room.
   */
  applyRemote<T>(fn: () => T): T {
    const prev = this.suppressBroadcast;
    this.suppressBroadcast = true;
    try { return fn(); } finally { this.suppressBroadcast = prev; }
  }

  private handleMessage(data: unknown): void {
    if (typeof data !== 'string') return;
    let msg: unknown;
    try { msg = JSON.parse(data); } catch { return; }
    if (!msg || typeof msg !== 'object') return;
    const m = msg as { type?: string };

    if (m.type === 'welcome') {
      const w = m as unknown as { you: Peer; clients: Peer[] };
      this.me = w.you;
      this.others = w.clients.filter(c => c.id !== w.you.id);
      this.lastRosterSize = w.clients.length;
      this.handlers.onPresence(this.me, this.others);
      // Tell the room our name (if any) so existing peers stop showing us as Anon.
      this.maybeBroadcastName();
      return;
    }
    if (m.type === 'presence') {
      const p = m as unknown as { clients: Peer[] };
      const grew = p.clients.length > this.lastRosterSize;
      this.lastRosterSize = p.clients.length;
      this.others = p.clients.filter(c => !this.me || c.id !== this.me.id);
      this.handlers.onPresence(this.me, this.others);
      // A new peer just joined — re-announce our name so they learn it (the
      // relay has no state to replay our earlier announcement from).
      if (grew) this.maybeBroadcastName();
      return;
    }
    if (m.type === 'delta') {
      const d = m as unknown as { payload: Delta; from: Peer };
      if (!d.payload || !d.from) return;
      this.applyRemote(() => this.handlers.onRemoteDelta(d.payload, d.from));
      return;
    }
    if (m.type === 'emoji') {
      const e = m as unknown as { peerId?: string; emoji?: string };
      if (typeof e.peerId === 'string' && typeof e.emoji === 'string') {
        this.handlers.onEmoji(e.peerId, e.emoji);
      }
      return;
    }
    if (m.type === 'sync-request') {
      const r = m as unknown as { from: string };
      try {
        const fullDelta = this.handlers.onSyncRequest();
        this.socket?.send(JSON.stringify({ type: 'sync-state', payload: fullDelta, to: r.from }));
      } catch { /* noop */ }
      return;
    }
    if (m.type === 'sync-state') {
      const s = m as unknown as { payload: Delta };
      this.applyRemote(() => this.handlers.onSyncState(s.payload));
      return;
    }
  }
}

/** Parses `room=` from a hash string. Returns null if absent. */
export function parseRoom(hashStr?: string): string | null {
  const raw = hashStr ?? window.location.hash;
  const hash = raw.startsWith('#') ? raw.slice(1) : raw;
  if (!hash) return null;
  const params = new URLSearchParams(hash);
  const room = params.get('room');
  return room && /^[a-zA-Z0-9_-]{4,40}$/.test(room) ? room : null;
}

/** Generates a short room id (12 chars, URL-safe). */
export function generateRoomId(): string {
  // crypto.randomUUID is available in all modern browsers / over HTTPS.
  const uuid = (crypto.randomUUID?.() ?? Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2));
  // Strip dashes and take first 12 chars for a friendlier-looking id.
  return uuid.replace(/-/g, '').slice(0, 12);
}
